import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { classNames } from "@via/utils/helpers.tsx";
import { ComponentProps, useEffect } from "react";
import { Control, useController } from "react-hook-form";
import { Tooltip } from "react-tooltip";

type TextInputProps = ComponentProps<"input"> & {
  control: Control<any>;
  label?: string;
  name: string;
  validations?: any;
  className?: string;
  showRequiredField?: boolean;
  showTooltipIcon?: boolean;
  tooltipText?: string;
  type?: string;
};

export default function FormInputComponent({
  control,
  label,
  name,
  validations,
  className = "",
  showRequiredField = false,
  showTooltipIcon = false,
  tooltipText,
  type = "text",
  ...inputProps
}: TextInputProps) {
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: validations,
    ...(type === "number"
      ? {
          setValueAs: value => (value === "" ? null : Number(value)),
        }
      : type === "checkbox"
        ? {
            setValueAs: value => value === "true" || value === true,
          }
        : type === "date"
          ? {
              setValueAs: value => (value ? new Date(value) : null),
            }
          : {}),
  });

  // Transform initial value for number types if it is a non-empty string
  useEffect(() => {
    const currentValue = field.value;
    if (
      type === "number" &&
      typeof currentValue === "string" &&
      currentValue !== ""
    ) {
      const numValue = Number(currentValue);
      // Only call onChange if it's a valid number
      if (!isNaN(numValue)) {
        field.onChange(numValue);
      }
    }
  }, []);

  return (
    <div className="mt-2">
      {label && (
        <label className="mb-2 flex items-center text-sm font-medium leading-6 text-gray-900">
          {label}
          {showRequiredField && <span className="text-red-400">&nbsp;*</span>}
          {showTooltipIcon && (
            <InformationCircleIcon
              id={`tooltip-${name.replace(/\./g, "")}`}
              className="h-5 w-5 ml-1"
            />
          )}
        </label>
      )}
      {tooltipText && (
        <Tooltip anchorSelect={`#tooltip-${name.replace(/\./g, "")}`}>
          <div>
            {tooltipText.split("<br/>").map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        </Tooltip>
      )}
      <input
        type={type}
        className={classNames(
          "block rounded-md shadow-sm focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
          type === "checkbox"
            ? "border-gray-300 focus:border-indigo-600 text-indigo-600 focus:ring-1 focus:ring-offset-0 cursor-pointer"
            : "border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2",
          !className?.match(/\bw-\S+/) ? "w-full" : "", // add w-full only if no w-* present
          className
        )}
        {...field}
        checked={type === "checkbox" ? !!field.value : undefined}
        value={type === "checkbox" ? undefined : (field.value ?? "")}
        onChange={e => {
          if (type === "checkbox") {
            field.onChange(e.target.checked);
          } else if (type === "number") {
            const value = e.target.value;
            field.onChange(value === "" ? null : Number(value));
          } else {
            field.onChange(e.target.value);
          }
        }}
        {...inputProps}
      />
      {error && <span className="text-xs text-rose-500">{error.message}</span>}
    </div>
  );
}
